import { getDeviceProperty } from '@/api';
import {
  deviceControl,
  devicePropertySet,
  getDevicePropertyCoefficientList,
  setDevicePropertyCoefficient,
  DeviceProperty,
} from '@/api/device-thingmodel';
import { AppstoreOutlined, EditOutlined, HistoryOutlined, LineChartOutlined, SettingOutlined } from '@ant-design/icons';
import { getFilterPriceList, FilterPriceItem } from '@/api/customer';

import { Card, Col, Divider, Empty, Form, Input, InputNumber, message, Modal, Pagination, Radio, Row, Select, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import { isNull, isObject } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import MatrixChart from './MatrixChart';
import { Tab1Modal, Tab1ModalRef } from './Tab1Modal';
import { WriteValueModal } from './WriteValueModal';

const { Text, Title } = Typography;

/** 属性code → 滤芯code 映射（价格管理里配置的 valid_days） */
const PROP_TO_FILTER_CODE: Record<string, string> = {
  UF_time: 'UF',
  CM_time: 'CM',
  remain_days: 'RD',
};

/** 默认倒计时天数（价格管理里没配时使用） */
const DEFAULT_VALID_DAYS: Record<string, number> = {
  UF: 365,
  CM: 180,
  RD: 365,
};

export interface DetailTab1Props {
  product_id?: string;
  valveLocked?: boolean;
  isActivated?: boolean;
}

export const DetailTab1: React.FC<DetailTab1Props> = (props) => {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const timer = useRef<ReturnType<typeof setInterval>>();
  const tab1ModalRef = useRef<Tab1ModalRef>(null);

  const [lists, setLists] = useState<DeviceProperty[]>([]);
  const [checked, setChecked] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('list');

  const [pageParams, setPageParams] = useState<{ page: number; pageSize: number; info?: string; type?: 'info' | 'tag' }>({ page: 1, pageSize: 16 });
  const pageParamsRef = useRef<{ page: number; pageSize: number; info?: string; type?: 'info' | 'tag' }>({ page: 1, pageSize: 16 });

  const [writeModalVisible, setWriteModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<DeviceProperty>();
  const [filterValidDays, setFilterValidDays] = useState<Record<string, number>>({});

  // 属性换算系数 k
  const [coeffMap, setCoeffMap] = useState<Record<string, number>>({});
  const [coeffModalVisible, setCoeffModalVisible] = useState(false);
  const [coeffSaving, setCoeffSaving] = useState(false);
  const [coeffItem, setCoeffItem] = useState<DeviceProperty>();
  const [coeffValue, setCoeffValue] = useState<number>(1);

  const loadCoefficients = useCallback(() => {
    getDevicePropertyCoefficientList(params.id!).then((resp: any) => {
      if (resp.success && Array.isArray(resp.result)) {
        const map: Record<string, number> = {};
        resp.result.forEach((item: any) => {
          map[item.code] = item.coefficient;
        });
        setCoeffMap(map);
      }
    }).catch(() => {});
  }, [params]);

  // 打开系数设置弹窗
  const openCoeffModal = (item: DeviceProperty) => {
    setCoeffItem(item);
    setCoeffValue(coeffMap[item.code] || 1);
    setCoeffModalVisible(true);
  };

  // 保存系数
  const saveCoefficient = async () => {
    if (!coeffItem) return;
    setCoeffSaving(true);
    try {
      const res: any = await setDevicePropertyCoefficient({
        device_id: params.id!,
        code: coeffItem.code,
        coefficient: coeffValue,
      });
      if (res.success) {
        message.success('保存成功');
        setCoeffModalVisible(false);
        loadCoefficients();
        load();
      } else {
        message.error(res.errorMsg || '保存失败');
      }
    } catch (error) {
      console.error(error);
      message.error('保存失败');
    } finally {
      setCoeffSaving(false);
    }
  };

  // 加载价格管理里配置的倒计时天数
  useEffect(() => {
    getFilterPriceList().then((resp: any) => {
      if (resp.success && resp.result) {
        const map: Record<string, number> = {};
        (resp.result as FilterPriceItem[]).forEach((item) => {
          if (item.filterCode && item.validDays > 0) {
            map[item.filterCode] = item.validDays;
          }
        });
        setFilterValidDays(map);
      }
    }).catch(() => {});
  }, []);

  // 获取属性对应的倒计时天数
  const getCountdownDays = (propCode: string): number => {
    const filterCode = PROP_TO_FILTER_CODE[propCode] || propCode;
    return filterValidDays[filterCode] || DEFAULT_VALID_DAYS[filterCode] || 365;
  };

  // 计算滤芯剩余天数
  const getRemainingDays = (propCode: string, timestampValue: number | string): number | null => {
    const ts = Number(timestampValue);
    if (!ts || isNaN(ts)) return null;
    const tsMs = ts > 1e12 ? ts : ts * 1000;
    const startDate = new Date(tsMs);
    const now = new Date();
    const elapsedDays = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const totalDays = getCountdownDays(propCode);
    return Math.max(0, totalDays - elapsedDays);
  };

  const load = useCallback(
    (changeParams?: { page?: number; pageSize?: number; info?: string; type?: 'info' | 'tag' }) => {
      const base = pageParamsRef.current;
      console.log({ changeParams });
      const { type, info, ...rest } = { ...base, ...changeParams };
      getDeviceProperty({ id: params.id!, ...rest, ...(type === 'tag' ? { tag: info } : { info }) }).then((resp) => {
        console.log(resp, { changeParams });
        if (resp.success) {
          setLists(resp.result.list);
          setTotal(resp.result.total);

          const code = searchParams.get('code');

          if (code) {
            const item = resp.result.list.find((item) => item.code === code);

            item && setTimeout(() => tab1ModalRef.current?.open(item), 0);
          }
        }
      });
    },
    [params, searchParams],
  );

  useEffect(() => {
    if (checked) {
      timer.current = setInterval(() => {
        load();
      }, 3000);
    } else {
      clearInterval(timer.current);
    }

    return () => {
      clearInterval(timer.current);
    };
  }, [checked]);

  useEffect(() => {
    // 初始化 info（来源于 url: ?code=xxx）并保存到 ref/state
    const initialInfo = searchParams.get('code') || undefined;
    if (initialInfo) {
      pageParamsRef.current = { ...pageParamsRef.current, info: initialInfo };
      setPageParams((prev) => ({ ...prev, info: initialInfo }));
      load({ info: initialInfo });
    } else {
      load();
    }
    loadCoefficients();
  }, []);

  // if (!lists.length) {
  //   return <Empty />;
  // }

  const handleChange = async (item: DeviceProperty) => {
    if (item.code === 'valve') {
      if (!isActivated) { message.warning('设备未激活，无法控制电磁阀'); return; }
      if (valveLocked) { message.warning('电磁阀已锁止，请先解锁'); return; }
    }
    setLoading((prev) => ({ ...prev, [item.code]: true }));
    try {
      const res = await deviceControl({ deviceId: params.id!, data: { [item.code]: !item.value } });
      if (res.success) {
        message.success('操作成功');
        load();
      } else {
        message.error(res.errorMsg);
      }
      setLoading((prev) => ({ ...prev, [item.code]: false }));
    } catch (error) {
      setLoading((prev) => ({ ...prev, [item.code]: false }));
      console.log(error);
    }
  };

  const { product_id, valveLocked, isActivated } = props;

  const handleWriteValue = async (value: string | number | boolean) => {
    if (!currentItem) return;

    // 电磁阀控制：锁止或未激活时禁止操作
    if (currentItem.code === 'valve') {
      if (!isActivated) { message.warning('设备未激活，无法控制电磁阀'); return; }
      if (valveLocked) { message.warning('电磁阀已锁止，请先激活设备解锁'); return; }
    }

    // date类型走property/set（不需要设备在线），其他类型走control（需要MQTT下发）
    const isDateType = currentItem.data_type?.toLowerCase() === 'date';
    const api = isDateType ? devicePropertySet : deviceControl;
    const msg = isDateType ? '正在保存...' : '正在下发指令...';

    const hide = message.loading(msg, 0);
    try {
      const res = await api({ deviceId: params.id!, data: { [currentItem.code]: value } } as any);
      hide();
      if (res.success) {
        message.success('写入成功');
        load();
        setWriteModalVisible(false);
      } else {
        message.error(res.errorMsg || '写入失败');
      }
    } catch (error) {
      hide();
      message.error(isDateType ? '保存失败' : '指令下发超时，请确认设备在线');
      console.error(error);
    }
  };

  return (
    <>
      <Tab1Modal ref={tab1ModalRef} product_id={product_id}></Tab1Modal>
      <WriteValueModal open={writeModalVisible} onCancel={() => setWriteModalVisible(false)} onOk={handleWriteValue} item={currentItem} />
      <Modal
        title={`设置换算系数 (${coeffItem?.name || ''})`}
        open={coeffModalVisible}
        okButtonProps={{ loading: coeffSaving }}
        onCancel={() => setCoeffModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={360}
        onOk={saveCoefficient}
      >
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
            换算公式：y = kx，默认 k = 1。设置后所有展示位置（卡片/历史/图表/小程序）均按此系数换算。
          </div>
          <InputNumber
            value={coeffValue}
            onChange={(v) => setCoeffValue(v as number)}
            min={0}
            step={0.1}
            precision={4}
            style={{ width: '100%' }}
            placeholder="请输入 k 值"
          />
        </div>
      </Modal>
      <div className="mb10" style={{ display: 'flex', justifyContent: viewMode === 'list' ? 'space-between' : 'flex-end', alignItems: 'center', gap: '16px' }}>
        {viewMode === 'list' && (
          <Form layout="inline" initialValues={{ code: searchParams.get('code'), type: 'info' }}>
            <Form.Item name="type">
              <Select
                allowClear={false}
                options={[
                  { label: '名称', value: 'info' },
                  { label: '标签', value: 'tag' },
                ]}
                style={{ width: 100 }}
              />
            </Form.Item>
            <Form.Item dependencies={['type']} style={{ width: 200 }}>
              {({ getFieldValue }) => (
                <Input.Search
                  width={200}
                  placeholder="请输入查询条件"
                  allowClear
                  onSearch={(info) => {
                    const type = getFieldValue('type');
                    console.log({ type, info });
                    const searchInfo = info?.trim() || undefined;
                    pageParamsRef.current = { ...pageParamsRef.current, page: 1, info: searchInfo, type };
                    setPageParams({ ...pageParamsRef.current, type });
                    load({ page: 1, info: searchInfo, type });
                  }}
                ></Input.Search>
              )}
            </Form.Item>
          </Form>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <Radio.Button value="list" style={{ lineHeight: '34px' }}>
              <AppstoreOutlined style={{ fontSize: '16px' }} />
            </Radio.Button>
            <Radio.Button value="chart" style={{ lineHeight: '34px' }}>
              <LineChartOutlined style={{ fontSize: '16px' }} />
            </Radio.Button>
          </Radio.Group>
          <Form layout="inline">
            <Form.Item label="实时刷新">
              <Switch checked={checked} onChange={setChecked} />
            </Form.Item>
          </Form>
        </div>
      </div>
      {viewMode === 'list' ? (
        <>
          {lists?.length === 0 && <Empty />}
          <Row gutter={[16, 16]}>
            {lists.map((item, index) => {
              return (
                <Col span={6} key={index}>
                  <Card className="property-card" hoverable size="small">
                    <div className="justify-space-between">
                      <div>
                        <Text style={{ color: '#000000', fontSize: 14 }}>{item.name}</Text>
                      </div>
                      <div>
                        <Text
                          className="on-hover"
                          style={{ fontSize: 12, color: '#000000' }}
                          onClick={() => {
                            tab1ModalRef.current?.open(item);
                          }}
                        >
                          <HistoryOutlined className="mr5" />
                          历史数据
                        </Text>
                      </div>
                    </div>
                    <div className="mt10 mb20 align-center justify-space-between">
                      {PROP_TO_FILTER_CODE[item.code] ? (
                        <div style={{ textAlign: 'center' }}>
                          {(() => {
                            const remainingDays = getRemainingDays(item.code, item.value as number | string);
                            if (remainingDays === null) {
                              return <Title level={5} style={{ fontSize: 18, margin: 0, color: '#999' }}>未设置</Title>;
                            }
                            const isExpired = remainingDays <= 30;
                            return (
                              <>
                                <Title level={5} style={{ fontSize: 24, margin: 0, color: isExpired ? '#ff4d4f' : '#1890ff' }}>
                                  {remainingDays}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>天（剩余）</Text>
                              </>
                            );
                          })()}
                        </div>
                      ) : item.data_type === 'date' && !isNull(item.value) ? (
                        // date: 显示日期 + 剩余天数
                        <div>
                          {(() => {
                            const ts = Number(item.value);
                            const months = item.code === 'CLM_time' ? 12 : item.code === 'PPM_time' ? 6 : item.code === 'CMYSHXT_time' ? 6 : 12;
                            const totalDays = months * 30;
                            const elapsedDays = Math.floor((Date.now() / 1000 - ts) / 86400);
                            const remainingDays = Math.max(0, totalDays - elapsedDays);
                            return (
                              <>
                                <Title level={5} ellipsis={{ tooltip: dayjs(ts * 1000).format('YYYY-MM-DD HH:mm:ss') }} style={{ fontSize: 18, margin: 0 }}>
                                  {dayjs(ts * 1000).format('YYYY-MM-DD HH:mm')}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  剩余 {remainingDays} 天
                                </Text>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <Title level={5} ellipsis={{ tooltip: isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value) }} style={{ fontSize: 18, margin: 0 }}>
                          {isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value)}
                          {item.unit && <> ({item.unit})</>}
                        </Title>
                      )}
                      <div className="align-center">
                        {item?.data_type !== 'bool' && item?.access_mode?.toLowerCase() === 'rw' && (
                          <EditOutlined
                            style={{ fontSize: '14px', cursor: 'pointer' }}
                            onClick={() => {
                              setCurrentItem(item);
                              setWriteModalVisible(true);
                            }}
                          />
                        )}
                        {item?.data_type === 'bool' && item?.access_mode?.toLowerCase() === 'rw' && (
                          <Switch key={item.code} checked={!!lists.find((v) => v.code === item.code)?.value} onChange={() => handleChange(item)} loading={loading[item.code]} className="ml10" disabled={item.code === 'valve' && (!isActivated || valveLocked)} />
                        )}
                      </div>
                    </div>
                    <div className="mt20 justify-space-between" style={{ position: 'relative' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}
                        <Divider type="vertical" />
                        {item.data_type}
                        <Divider type="vertical" />
                        {item.access_mode?.toLowerCase() === 'ro' || item.access_mode?.toLowerCase() === 'r' ? '只读' : '读写'}
                        {coeffMap[item.code] ? (
                          <>
                            <Divider type="vertical" />
                            k={coeffMap[item.code]}
                          </>
                        ) : null}
                      </Text>
                      <SettingOutlined
                        title="设置换算系数 k（y=kx）"
                        style={{ fontSize: '14px', cursor: 'pointer', color: '#8c8c8c' }}
                        onClick={() => openCoeffModal(item)}
                      />
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      ) : (
        <>
          {lists?.length === 0 && <Empty />}
          <MatrixChart height={600} deviceId={params.id!} />
        </>
      )}
      {viewMode === 'list' && (
        <div className="mt20" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={pageParams.page}
            pageSize={pageParams.pageSize}
            onChange={(page, pageSize) => {
              pageParamsRef.current = { ...pageParamsRef.current, page, pageSize };
              setPageParams({ ...pageParamsRef.current });
              load({ page, pageSize });
            }}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
            showQuickJumper
            showPrevNextJumpers
            total={total}
          ></Pagination>
        </div>
      )}
    </>
  );
};

export default DetailTab1;
